import { ContextualInfoButtonWithText } from "@/components/buttons/ContextualInfoButton";
import { SelectWithDescriptions, SelectWithDescriptionsProps } from "@/components/inputs/SelectWithDescriptions";
import { getArchetypes, getBaseThemeName } from "@/features/branding";
import { RiskBandsConfig } from "@/features/risk_bands";
import { RiskBandsArchetype } from "@/tools/risk-bands/types";
import { Alert, Cascader, CascaderProps } from "antd";
import classNames from "classnames";
import { useEffect, useState } from "react";
import { getI18n } from "react-i18next";
import styles from "./RiskBandsAdvancedSettings.module.scss";
interface RiskBandsArchetypeSelectorProps {
  archetype: string;
  onChange: (value: RiskBandsArchetype) => void;
}
export const RiskBandsArchetypeSelector = ({
  archetype,
  onChange
}: RiskBandsArchetypeSelectorProps) => {
  const archetypes = getArchetypes().map(archetype => ({
    ...archetype,
    id: archetype.value
  }));
  const defaultArchetype = archetypes.find(({
    id
  }) => id === archetype) || archetypes[0];
  const [archetypeWarning, setArchetypeWarning] = useState(defaultArchetype.warning);
  const [isSearching, setIsSearching] = useState(false);
  useEffect(() => {
    const {
      warning
    } = archetypes.find(({
      id
    }) => id === archetype)!;
    setArchetypeWarning(warning);
  }, [archetype]);
  const cascaderFilterOption = (inputValue: string, path: any[]) => {
    return path.some(option => option.label.toLowerCase().indexOf(inputValue.toLowerCase()) > -1);
  };
  const cascaderDisplayRender: CascaderProps["displayRender"] = (labels, selectedOptions = []) => {
    const lastOption = selectedOptions[selectedOptions.length - 1];
    const parentPath = labels.slice(0, -1).join(" / ");
    if (!lastOption) {
      return null;
    }
    return <div key={lastOption.value} className="flex flex-col gap-y-0.5" style={{
      opacity: "inherit"
    }} data-sentry-component="cascaderDisplayRender" data-sentry-source-file="RiskBandsArchetypeSelector.tsx">
        <div className="text-xs opacity-50">{parentPath}</div>
        <div className="overflow-y-visible text-sm leading-tight">{lastOption.label}</div>
      </div>;
  };
  const onCascaderChange = (props: string[]) => {
    if (!(props && props.length)) {
      return;
    }
    const cascaderArchetype = props[props.length - 1] as RiskBandsArchetype;
    if (cascaderArchetype) onChange(cascaderArchetype);
  };
  return <>
      {getBaseThemeName() === "hkma" ? <SelectWithDescriptions aria-label="Asset Type (Archetype)" onChange={(props: RiskBandsArchetype) => {
      const selectedArchetype = props as RiskBandsArchetype;
      if (selectedArchetype && selectedArchetype.length) onChange(selectedArchetype);
    }} options={buildArchetypeSelectProps(archetypes)} value={archetype} /> : <Cascader aria-label="Asset Type (Archetype)" options={buildArchetypesCascaderProps(archetypes)} allowClear={false} className="h-12 w-full" placeholder="Please select" classNames={{
      popup: {
        root: classNames(styles.cascader, `${isSearching ? "w-max" : "w-[600px]"}`)
      }
    }} showSearch={{
      filter: cascaderFilterOption
    }} onSearch={val => setIsSearching(val.length > 0)} displayRender={cascaderDisplayRender} onChange={onCascaderChange} onOpenChange={open => !open && setIsSearching(false)} value={archetype ? (() => {
      const selectedArchetype = archetypes.find(({
        value
      }) => value === archetype);
      return selectedArchetype ? [selectedArchetype.category, selectedArchetype.service || "Other", archetype] : undefined;
    })() : undefined} />}

      <div className="pl-3 pr-0 pt-2 text-xs text-gray-500">
        <ContextualInfoButtonWithText className="m-0 h-max p-0" rootClassName="flex items-center" id={(() => {
        const selectedArchetype = archetypes.find(({
          value
        }) => value === archetype);
        if (!selectedArchetype?.embed) return "";
        return "id" in selectedArchetype.embed ? selectedArchetype.embed.id : "";
      })()} data-sentry-element="ContextualInfoButtonWithText" data-sentry-source-file="RiskBandsArchetypeSelector.tsx">
          {archetypes.find(({
          value
        }) => value === archetype)?.description}
        </ContextualInfoButtonWithText>
      </div>

      {archetypeWarning && <Alert type="warning" message={archetypeWarning} showIcon style={{
      marginTop: ".5rem"
    }} />}
    </>;
};
const buildArchetypeSelectProps = (archetypes: RiskBandsConfig["archetypes"]) => {
  // Use the previous shape: category > archetype
  const categorySet = new Set();
  archetypes.forEach(({
    category
  }) => categorySet.add(category));
  if (categorySet.size > 1) {
    return archetypes.reduce((list, archetype) => {
      let isListChanged = false;
      const updatedList = list.map(item => {
        // If the OptionGroup already exist, just append archetype to it.
        if (item.id === archetype.category && !isListChanged) {
          isListChanged = true;
          return {
            ...item,
            options: [...(item.options || []), archetype]
          };
        }
        return item;
      });

      // If OptionGroup for archetype does not yet exist, create it.
      return isListChanged ? updatedList : [...list, {
        category: archetype.category,
        id: archetype.category,
        value: archetype.category,
        name: {
          residential: "Residential",
          commercial: "Commercial",
          industrial: "Industrial"
        }[archetype.category],
        options: [archetype]
      }];
    }, [] as SelectWithDescriptionsProps["options"]);
  }
  return archetypes;
};
const buildArchetypesCascaderProps = (archetypes: RiskBandsConfig["archetypes"]) => {
  const t = getI18n().getFixedT(null, "terms", "archetypeCategories");

  // Use the new shape: category > service > archetype
  const groupedArchetypes = archetypes.reduce((acc, archetype) => {
    const category = archetype.category;
    const service = archetype.service || "Other";
    if (!acc[category]) {
      acc[category] = {};
    }
    if (!acc[category][service]) {
      acc[category][service] = [];
    }
    acc[category][service].push(archetype);
    return acc;
  }, {} as Record<string, Record<string, typeof archetypes>>);
  return Object.entries(groupedArchetypes).map(([category, services]) => ({
    id: category,
    value: category,
    label: t(category) || category,
    children: Object.entries(services).map(([service, archetypes]) => ({
      id: `${category}-${service}`,
      value: service,
      label: service,
      children: archetypes.map(archetype => ({
        id: archetype.id,
        value: archetype.value,
        label: archetype.name,
        description: archetype.description
      }))
    }))
  }));
};